import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";
import verqoLogo from "@/assets/verqo-logo.png.asset.json";
import verqoIcon from "@/assets/verqo-icon.png.asset.json";
import {
  MANUAL_IMPORT_NOTE,
  PRICE_FILE_SUPPLIER_EXAMPLES,
  PRICE_IMPORT_HEADLINE,
  PRICE_IMPORT_STEPS,
  SUPPORTED_PRICE_FILE_LABEL,
} from "@/lib/price-file-import";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "VERQO – Fra pris til faktura i én arbeidsflyt" },
      {
        name: "description",
        content:
          "VERQO samler leverandørpriser, materiell, kunder, tilbud, prosjekter og faktura i én arbeidsflyt for håndverksbedrifter.",
      },
      { property: "og:title", content: "VERQO – Fra pris til faktura i én arbeidsflyt" },
      {
        property: "og:description",
        content:
          "Hold orden på kunder, leverandørpriser, materiell, tilbud, prosjekter og faktura – i én arbeidsflyt.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Landing,
});

function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard", replace: true });
    });
  }, [navigate]);

  const flowSteps = [
    { label: "Leverandørpriser" },
    { label: "Materiell" },
    { label: "Tilbud" },
    { label: "Prosjekt" },
    { label: "Faktura" },
  ];

  const features = [
    {
      title: "Opplasting av prisfil",
      text: "Du laster ned vare-/prisfilen hos leverandøren din og laster den opp i VERQO. Manuell filimport – ingen automatisk henting.",
    },
    {
      title: "Kunder",
      text: "Hold oversikt over privat- og bedriftskunder med adresse, kontakt og prosjekter.",
    },
    {
      title: "Materiell og priser",
      text: "Ett register for alt materiell, med innkjøpspris, kundepris og varegrupper.",
    },
    {
      title: "Tilbud",
      text: "Bygg tilbud fra materiell og standardjobber, og send profesjonell PDF til kunden.",
    },
    {
      title: "Prosjekter",
      text: "Følg opp prosjekter med linjer, kalkyler og tidsbruk – alt koblet til tilbudet.",
    },
    {
      title: "Faktura",
      text: "Fakturer direkte fra tilbud eller prosjekt, med sporing av betaling og kreditnota.",
    },
    {
      title: "Oversikt og kontroll",
      text: "Se status, marginer og fremdrift på tvers av tilbud, prosjekter og faktura.",
    },
    {
      title: "Mindre dobbeltarbeid",
      text: "Data gjenbrukes gjennom hele arbeidsflyten – fra pris til faktura.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-4 sm:flex sm:justify-between sm:px-6 sm:py-7">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-4">
            <img src={verqoIcon.url} alt="VERQO" className="h-9 w-9 shrink-0 object-contain sm:h-[68px] sm:w-[68px]" />
            <span className="truncate text-sm font-semibold tracking-[0.24em] sm:text-lg">VERQO</span>
          </Link>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <Link to="/auth" className="btn btn-ghost px-2 text-xs sm:px-3 sm:text-sm">Logg inn</Link>
            <Link to="/auth" className="btn btn-primary px-3 text-xs sm:px-4 sm:text-sm">Kom i gang</Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-6 pt-14 pb-16 text-center sm:pt-36 sm:pb-24">
          <h1 className="mx-auto max-w-4xl text-4xl font-semibold tracking-tight sm:text-6xl">
            Fra pris til faktura.<br />
            <span className="text-muted-foreground">Alt samlet i VERQO.</span>
          </h1>
          <p className="mx-auto mt-10 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:mt-12 sm:text-xl">
            Hold orden på kunder, leverandørpriser, materiell, tilbud, prosjekter og faktura – i én arbeidsflyt.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:mt-14">
            <Link to="/auth" className="btn btn-primary px-6 py-3 text-base">
              Kom i gang
            </Link>
            <a href="#arbeidsflyt" className="btn btn-ghost px-6 py-3 text-base">
              Se arbeidsflyten
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>

        {/* Arbeidsflyt */}
        <section id="arbeidsflyt" className="border-t border-border/60">
          <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl font-semibold tracking-tight sm:text-4xl">
                Én arbeidsflyt – fem trinn
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Data flytter seg gjennom hele prosessen, uten dobbeltregistrering.
              </p>
            </div>

            <ol className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {flowSteps.map((s, i) => (
                <li key={s.label} className="flex items-center gap-3 sm:gap-4">
                  <div className="rounded-lg border border-border/70 bg-card px-5 py-3 text-sm font-medium sm:text-base">
                    {s.label}
                  </div>
                  {i < flowSteps.length - 1 && (
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground sm:h-5 sm:w-5" />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Prisfil */}
        <section className="border-t border-border/60">
          <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl font-semibold tracking-tight sm:text-4xl">
                Bruk dine egne leverandørpriser
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {PRICE_IMPORT_HEADLINE}
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                For eksempel fra {PRICE_FILE_SUPPLIER_EXAMPLES.join(", ")}.
              </p>
            </div>

            <ol className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {PRICE_IMPORT_STEPS.map((s, i) => (
                <li key={s} className="rounded-lg border border-border/70 bg-card p-5">
                  <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground">
                    TRINN {i + 1}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed">{s}</p>
                </li>
              ))}
            </ol>

            <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
              {MANUAL_IMPORT_NOTE} Filformat: {SUPPORTED_PRICE_FILE_LABEL}.
            </p>
          </div>
        </section>

        {/* Funksjoner */}
        <section className="border-t border-border/60">
          <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl font-semibold tracking-tight sm:text-4xl">
                Hva VERQO håndterer
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Funksjonene som inngår i arbeidsflyten.
              </p>
            </div>

            <div className="mt-12 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((f) => (
                <div key={f.title}>
                  <h3 className="text-base font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-5xl">
            Kom i gang med VERQO
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
            Én arbeidsflyt fra leverandørpris til faktura.
          </p>
          <div className="mt-10">
            <Link to="/auth" className="btn btn-primary px-7 py-3 text-base">
              Kom i gang
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 py-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} VERQO</span>
          <span className="tracking-[0.18em]">HÅNDVERKSPLATTFORM</span>
        </div>
      </footer>
    </div>
  );
}
